import { capitalize } from 'lodash'
import { sanitizeName } from 'app/sanitize-name'

/**
 * A machine identifier standing in for the question the patient actually read:
 * a slugified key ("what_kind_of_insurance?") or a JotForm "unique name"
 * ("whatIs4", "anyQuestions"). Anything containing a space is the vendor's own
 * prose and is left exactly as written.
 */
export const isMachineKey = (label: string): boolean =>
  !!label && !/\s/.test(label) && (label.includes(`_`) || /[a-z0-9][A-Z]/.test(label))

/** Split a machine key on its word boundaries: underscores, camel humps, letter→digit. */
export const splitMachineKey = (key: string): string[] =>
  key
    .replace(/_+/g, ` `)
    .replace(/([a-z0-9])([A-Z])/g, `$1 $2`)
    .replace(/([A-Za-z])(\d)/g, `$1 $2`)
    .split(/\s+/)
    .filter(Boolean)

/**
 * Sentence-case machine words. Acronym runs ("PPO", "SSN") keep their casing;
 * everything else reads as the sentence the vendor slugified.
 */
export const sentenceCase = (words: string[]): string =>
  words
    .map((word, index) => {
      if (word.length > 1 && /[A-Z]/.test(word) && word === word.toUpperCase()) return word
      return index === 0 ? capitalize(word) : word.toLowerCase()
    })
    .join(` `)

/**
 * Turn a machine key back into readable text ("are_you_ready_to_come_in?" →
 * "Are you ready to come in?"). Real prose questions pass through untouched.
 */
export const humanizeLabel = (label: string): string =>
  isMachineKey(label) ? sentenceCase(splitMachineKey(label)) : label

/**
 * Vendors deliver questions and answers with HTML-escaped entities
 * ("Exam &amp; X-Ray", "Bob&#039;s"). Decode after sanitizeName has stripped
 * real tags, so escaped text renders as the submitter wrote it.
 */
export const decodeHtmlEntities = (value: string): string => {
  if (!value.includes(`&`)) return value
  const el = document.createElement(`textarea`)
  el.innerHTML = value
  return el.value
}

/**
 * A machine key reduced to its stem: casing and punctuation dropped, plus the
 * collision counter JotForm appends when two fields share a name ("whatIs4"
 * and "whatIs5" both stem to "whatis").
 */
export const machineKeyStem = (key: string): string =>
  key
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ``)
    .replace(/\d+$/, ``)

/**
 * The label to show for a raw form field key: the vendor's own question text
 * when it has one, otherwise the machine key turned back into a sentence. Runs
 * the same sanitize → decode → humanize pipeline `FormCard` applies to answer
 * questions, so a consent field reads the same way wherever it appears.
 */
export const formFieldLabel = (rawKey: string): string => humanizeLabel(decodeHtmlEntities(sanitizeName(rawKey)))

/**
 * JotForm submits a consent field under both the question the patient read and
 * the field's internal unique name ("Can we text you about your appointment?"
 * and "textOk"). A field picker only needs one entry per question, so drop the
 * machine-keyed twin when a prose field's stem starts with it and either they
 * offer the same answers or neither has an answer list at all — two unrelated
 * fields that merely share answers, or a short stem ("q1") that would
 * prefix-match half the form, both survive.
 */
export function fieldsWithoutMachineTwins(fields: string[], answersByField: Map<string, string[]>): string[] {
  const proseFields = fields.filter((name) => !isMachineKey(name))
  const sameAnswers = (a: string[], b: string[]) => a.length === b.length && a.every((value) => b.includes(value))
  return fields.filter((name) => {
    if (!isMachineKey(name)) return true
    const stem = machineKeyStem(name)
    if (stem.length < 4) return true
    const answers = answersByField.get(name)
    return !proseFields.some((prose) => {
      if (!machineKeyStem(prose).startsWith(stem)) return false
      const proseAnswers = answersByField.get(prose)
      return answers && proseAnswers ? sameAnswers(answers, proseAnswers) : !answers && !proseAnswers
    })
  })
}
